import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import {
  TBctxItemWithoutSizeAndBctxKey,
  TWipPackerLIneMap,
  TWipPackerStore,
  TRESPONSE_BusinessContextData,
  TBCTXKeysWithoutSize,
  TBctxHeaderItem,
  TWipPackerCarton,
  TAboutObject,
  TWhenObject,
  TWhoObject,
  TWhatObject,
  TWipPackerSavePayload,
  TBCTXKeys,
  TWipPackerGradingViewDescriptor,
  TAppDetailsWithAdditionalInfo,
  Subject,
  TWipPackerSizeToWipMap,
} from '@monorepo/types';
import {
  getBctxItemWithoutSizeAndBctxKeyList,
  getCartonList,
  getCurrentCartons,
  getSelectedBctx,
  getSelectedBctxHeader,
  getOpenCurrentCartons,
  getFailedEvents,
  getCounts,
  getUndoList,
  getLineMap,
} from './wip-packer.query';
import { createEffect } from '@ngrx/effects';
import {
  CompleteCarton,
  DeleteCarton,
  InitWipPackerStore,
  LoadBctxData,
  SaveCarton,
  SetCurrentCarton,
  SetSelectedBctxAndHeader,
  UndoCarton,
} from './wip-packer.actions';
import { getBusinessContextDataPayload } from '../input-app-client-db/input-app-client-db.query';
import { BCTXComputationHelper } from '@monorepo/utils';
import { AppMasterDBFacade } from '../app-master-db/app-master-db.facade';
import { map, take, switchMap, of } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
@Injectable({
  providedIn: 'root',
})
export class WipPackerStoreFacade {
  constructor(private store: Store<TWipPackerStore>, private appMasterDBFacade: AppMasterDBFacade) {}

  initWipPackerStore() {
    this.store.dispatch(new InitWipPackerStore());
  }

  getBctxPayload() {
    return this.store.pipe(select(getBusinessContextDataPayload));
  }

  loadBctxData() {
    this.store.dispatch(new LoadBctxData());
  }

  getBctxList() {
    return this.store.pipe(select(getBctxItemWithoutSizeAndBctxKeyList));
  }

  getLineMapFromBctxData(
    bctxData: TRESPONSE_BusinessContextData,
    bctxKeysOrder: TBCTXKeysWithoutSize[],
    station: string,
  ) {
    let lineMap: TWipPackerLIneMap = {};
    bctxData.forEach((bctxData) => {
      const bctxItemListWithoutSize = BCTXComputationHelper.filterBctxItemsWithKeys(bctxKeysOrder, bctxData.bctx_items);
      const bctx_key = BCTXComputationHelper.getUniqueBctxKeyWithOrder(bctxKeysOrder, bctxItemListWithoutSize);

      const size = bctxData.bctx_items.find((bctxItem) => bctxItem.k === 'size')?.v;
      if (size) {
        if (!lineMap[station]) {
          lineMap[station] = {};
        }
        if (!lineMap[station][bctx_key]) {
          lineMap[station][bctx_key] = {};
        }
        lineMap[station][bctx_key][size] = bctxData.bctx_values.qty;
      }
    });

    return lineMap;
  }

  getCounts() {
    return this.store.pipe(select(getCounts));
  }

  getTotalPacksAndPackedPiecesForBctx() {
    return this.getSelectedBctx().pipe(
      switchMap((selectedBctx) => {
        return this.getGradingPageConfig().pipe(
          map((gradingPageConfig) => {
            const gradingPageViewDescriptor = gradingPageConfig as TWipPackerGradingViewDescriptor;
            const groupingKeys = gradingPageViewDescriptor.grouping_properties || [];
            return [selectedBctx, groupingKeys] as const;
          }),
        );
      }),
      switchMap(([selectedBctx, groupingKeys]) => {
        if (selectedBctx.length === 0) return of({ packedPacks: 0, packedPieces: 0 });
        const selectedGroupKey = this.getSelectedbctxForCarton(selectedBctx, groupingKeys);
        return this.getCounts().pipe(
          map((counts) => {
            return counts[selectedGroupKey.bctxKey] || { packedPacks: 0, packedPieces: 0 };
          }),
        );
      }),
    );
  }

  getSelectedbctxForCarton(
    selectedBctx: TBctxItemWithoutSizeAndBctxKey[],
    groupingKeys: TBCTXKeysWithoutSize[],
  ): TBctxItemWithoutSizeAndBctxKey {
    const bctxItemWithSizeAndColor = selectedBctx[0].list.filter((bctxItem) => bctxItem.k !== 'color');
    const bctxItemListWithoutSize = BCTXComputationHelper.filterBctxItemsWithKeys(
      groupingKeys,
      bctxItemWithSizeAndColor,
    );
    const bctx_key = BCTXComputationHelper.getUniqueBctxKeyWithOrder(groupingKeys, bctxItemListWithoutSize);
    return {
      bctxKey: bctx_key,
      list: bctxItemWithSizeAndColor,
    };
  }

  getGroupKeyForCarton(selectedBctx: TBctxItemWithoutSizeAndBctxKey, groupingKeys: TBCTXKeysWithoutSize[]): string {
    const bctxItemWithSizeAndColor = selectedBctx.list.filter((bctxItem) => groupingKeys.includes(bctxItem.k));
    const bctxItemListWithoutSize = BCTXComputationHelper.filterBctxItemsWithKeys(
      groupingKeys,
      bctxItemWithSizeAndColor,
    );
    const groupKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(groupingKeys, bctxItemListWithoutSize);
    return groupKey;
  }

  setSelectedBctxAndHeader(selectedBctx: TBctxItemWithoutSizeAndBctxKey[], selectedBctxHeader: TBctxHeaderItem[]) {
    this.store.dispatch(new SetSelectedBctxAndHeader(selectedBctx, selectedBctxHeader));
  }

  getSelectedBctxHeader() {
    return this.store.pipe(select(getSelectedBctxHeader));
  }

  getLineMap() {
    return this.store.pipe(select(getLineMap));
  }

  getSizeToWipMapForSelectedBctx() {
    return this.getLineMap().pipe(
      switchMap((lineMap) => {
        return this.getSelectedBctx().pipe(
          map((selectedBctx) => {
            return [lineMap, selectedBctx] as const;
          }),
        );
      }),
      switchMap(([lineMap, selectedBctx]) => {
        return this.appMasterDBFacade.getSelectedStation().pipe(
          map((station) => {
            let sizeToWipMap: TWipPackerSizeToWipMap = {};
            if (station?.subject && selectedBctx.length > 0 && lineMap[station.subject]) {
              sizeToWipMap = lineMap[station.subject][selectedBctx[0].bctxKey];
            }
            return sizeToWipMap;
          }),
        );
      }),
    );
  }

  getSelectedBctx() {
    return this.store.pipe(select(getSelectedBctx));
  }

  getCurrentCarton() {
    return this.store.pipe(select(getCurrentCartons));
  }

  setCurrentCarton(carton: TWipPackerCarton) {
    this.store.dispatch(new SetCurrentCarton(carton));
  }

  getCartonList() {
    return this.store.pipe(select(getCartonList));
  }

  getFailedEvents() {
    return this.store.pipe(select(getFailedEvents));
  }

  getOpenCurrentCartons() {
    return this.store.pipe(select(getOpenCurrentCartons));
  }

  getWipPackerSavePayload(carton: TWipPackerCarton) {
    carton = {
      ...carton,
      search_id: uuidv4(),
    };
    return this.appMasterDBFacade.getSubjectKey().pipe(
      take(1),
      switchMap((subjectKey) => {
        return this.appMasterDBFacade.getAppDetailsWithAdditionalInfo().pipe(
          take(1),
          map((appDetails) => [appDetails, subjectKey] as const),
        );
      }),
      switchMap(([appDetails, subjectKey]) => {
        return this.appMasterDBFacade
          .getSelectedStation()
          .pipe(map((station) => [appDetails, station, subjectKey] as const));
      }),
      switchMap(([appDetails, station, subjectKey]) => {
        return this.getCartonList().pipe(
          map((cartonList) => [appDetails, station, subjectKey, cartonList.length + 1] as const),
        );
      }),
      switchMap(([appDetails, station, subjectKey, id]) => {
        return this.getGradingPageConfig().pipe(
          map((gradingPageConfig) => [appDetails, station, subjectKey, id, gradingPageConfig] as const),
        );
      }),
      switchMap(([appDetails, station, subjectKey, id, gradingPageConfig]) => {
        const gradingPageViewDescriptor = gradingPageConfig as TWipPackerGradingViewDescriptor;
        const groupingKeys = gradingPageViewDescriptor.grouping_properties || [];

        const payload: TWipPackerSavePayload = {
          records: [
            {
              value: {
                v_id: carton.datetime.toString() + '_' + Math.random().toString(36).substr(2, 10),
                a: this.getAboutObject(carton, station, appDetails, groupingKeys),
                w1: this.getWhenObject(carton),
                w2: this.getWhoObject(appDetails, carton, id),
                w3: this.getWhatObject(carton),
                ei: {
                  dt: carton.datetime,
                  id: id,
                },
              },
              key: '1', // TODO: fighure it out what is this
            },
          ],
          subject_key: subjectKey,
          datetime: carton.datetime,
          server_validation: true,
          qty_validation: appDetails.qtyValidation,
          wipType: appDetails.wipType,
        };
        return of(payload);
      }),
    );
  }

  getAboutObject(
    carton: TWipPackerCarton,
    station: Subject | undefined,
    appDetails: TAppDetailsWithAdditionalInfo,
    keysToSend: TBCTXKeysWithoutSize[],
  ): TAboutObject {
    return {
      sub: station?.subject || '',
      ctx: carton.bctx[0].list.filter((item) => keysToSend.includes(item.k)),
      aux: {
        hsk: station?.hsk || 'primary',
        dnm: appDetails.dnm,
        dnr: appDetails.dnr,
      },
      ecx: {
        num: {
          pqty: carton.packTotal,
        },
        tag: {
          search_id: carton.search_id,
          pt_id: carton.search_id,
          sub_tags: [],
          src_ids: {
            search_id: null,
          },
          shift: appDetails.shift,
          shift_dn: appDetails.shiftDn,
        },
      },
      snm: appDetails.snm,
      dnrk: appDetails.dnrk,
    };
  }

  getWhenObject(carton: TWipPackerCarton): TWhenObject {
    const currentDate = new Date();
    return {
      dt: carton.datetime,
      tz: 'Asia/Colombo',
      os: currentDate.getTimezoneOffset(),
      cc: {
        i: carton.datetime,
        o: carton.datetime,
      },
    };
  }

  getWhoObject(appDetails: TAppDetailsWithAdditionalInfo, carton: TWipPackerCarton, id: number): TWhoObject {
    return {
      ad: appDetails.deviceId,
      at: appDetails.appType,
      an: appDetails.an,
      ur: {
        id: appDetails.urId,
        nm: appDetails.urNm,
      },
      epf: '',
      av: appDetails.appVersion,
      mi: carton.datetime.toString() + '-' + id,
    };
  }

  getWhatObject(carton: TWipPackerCarton): TWhatObject {
    const direction = carton.type === 'ADD' ? 1 : -1;
    return {
      ac: [
        {
          packing_data: {
            search_id: carton.search_id,
            k: ['color', 'size', 'vl', 'cv'],
            v: Object.values(carton.sizeToQtyMap).map((item) => [
              item.color,
              item.size,
              item.qty.toString(),
              carton.currentCartonCount * direction,
            ]),
          },
          pt_id: {
            category_id: '',
            a_ctx: 1,
            name: carton.search_id,
          },
        },
      ],
      cv: carton.currentCartonCount * direction,
      ty: 'category',
      nm: 'pack',
      vl: 1,
    };
  }

  saveCarton(carton: TWipPackerCarton, index: number) {
    this.store.dispatch(new SaveCarton(carton, index));
  }

  completeCarton(carton: TWipPackerCarton, index: number) {
    this.store.dispatch(new CompleteCarton(carton, index));
  }

  deleteCarton(carton: TWipPackerCarton, index: number) {
    this.store.dispatch(new DeleteCarton(carton, index));
  }

  getAllViewDescriptor() {
    return this.appMasterDBFacade.getAllViewDescriptor();
  }

  getGradingPageConfig() {
    return this.appMasterDBFacade.getAllViewDescriptor().pipe(
      take(1),
      map((viewDescriptors) => {
        return (
          viewDescriptors?.find((viewDescriptor) => viewDescriptor.view_name === 'pkg_simple_grading_page') || null
        );
      }),
    );
  }

  getPageConfigByViewName(viewName: string) {
    return this.appMasterDBFacade.getAllViewDescriptor().pipe(
      take(1),
      map((viewDescriptors) => {
        return viewDescriptors?.find((viewDescriptor) => viewDescriptor.view_name === viewName) || null;
      }),
    );
  }

  undoCarton() {
    this.store.dispatch(new UndoCarton());
  }

  getUndoList() {
    return this.store.pipe(select(getUndoList));
  }
}
